import { traduzirErroSupabase } from '@octawash/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ChevronLeft, Clock, Gift, MapPin, Store, Ticket } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { tempoDecorrido } from '@/lib/format';
import { supabase } from '@/lib/supabase';

/**
 * /resgatar — catalogo de ofertas de parceiros externos.
 * Sprint 16c. Cliente escolhe oferta, confirma, recebe codigo unico de 8 chars.
 *
 * /meus-cupons — lista de resgates ATIVOS pra mostrar QR/codigo no parceiro.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb = supabase as any;

interface OfertaAtiva {
  seq_oferta_parceiro: number;
  nom_oferta: string;
  des_oferta: string | null;
  des_url_foto: string | null;
  qtd_pontos_custo: number;
  val_face_cliente: number | null;
  qtd_dias_validade: number;
  seq_parceiro_resgate: number;
  nom_parceiro: string;
  cod_categoria: string;
  des_endereco: string | null;
}

interface ResgateResposta {
  seq_resgate_parceiro: number;
  cod_resgate: string;
  cod_resgate_formatado: string;
  dta_validade: string;
  qtd_pontos_debitados: number;
  nom_oferta: string;
  nom_parceiro: string;
}

const CATEGORIA_LABEL: Record<string, string> = {
  RESTAURANTE: 'Restaurante',
  MERCADO: 'Mercado',
  FARMACIA: 'Farmácia',
  PADARIA: 'Padaria',
  LOJA: 'Loja',
  OUTRO: 'Outro',
};

export function ResgatarPage() {
  const queryClient = useQueryClient();
  const ofertas = useQuery({
    queryKey: ['ofertas-parceiro-ativas'],
    queryFn: async () => {
      const { data, error } = await sb
        .from('vw_ofertas_parceiro_ativas')
        .select('*')
        .order('qtd_pontos_custo');
      if (error) throw error;
      return (data ?? []) as OfertaAtiva[];
    },
  });

  const [confirmando, setConfirmando] = useState<OfertaAtiva | null>(null);
  const [resgateNovo, setResgateNovo] = useState<ResgateResposta | null>(null);

  const resgatar = useMutation({
    mutationFn: async (seq: number) => {
      const { data, error } = await sb.rpc('fn_resgatar_oferta_parceiro', {
        p_seq_oferta_parceiro: seq,
      });
      if (error) throw error;
      return data as ResgateResposta;
    },
    onSuccess: (r) => {
      setResgateNovo(r);
      setConfirmando(null);
      void queryClient.invalidateQueries({ queryKey: ['meus-resgates-parceiro'] });
      void queryClient.invalidateQueries({ queryKey: ['fidelidade-saldo'] });
      toast.success('Resgate confirmado! Mostre o codigo no parceiro.');
    },
    onError: (e: Error) =>
      toast.error('Falha no resgate', { description: traduzirErroSupabase(e) }),
  });

  return (
    <div className="space-y-5 p-4 pb-24">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild className="text-muted-foreground -ml-2 h-8 px-2">
          <Link to="/" aria-label="Voltar">
            <ChevronLeft className="mr-1 h-4 w-4" />
            Voltar
          </Link>
        </Button>
        <Button variant="ghost" size="sm" asChild className="ml-auto">
          <Link to="/meus-cupons">
            <Ticket className="mr-2 h-4 w-4" />
            Meus cupons
          </Link>
        </Button>
      </div>

      <header className="space-y-1">
        <h1 className="text-display-md font-extrabold tracking-tight">Trocar pontos</h1>
        <p className="text-muted-foreground text-sm">
          Ofertas exclusivas de parceiros locais. Use seus pontos para descontos e brindes.
        </p>
      </header>

      {ofertas.isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
      ) : (ofertas.data?.length ?? 0) === 0 ? (
        <EmptyState
          variant="hero"
          icon={Gift}
          title="Nenhuma oferta disponivel agora"
          description="Quando seu lava-jato ou posto cadastrar ofertas de parceiros, aparecem aqui."
        />
      ) : (
        <div className="space-y-3">
          {ofertas.data!.map((o) => (
            <motion.div
              key={o.seq_oferta_parceiro}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <Card variant="elevated" className="overflow-hidden rounded-2xl">
                <CardContent className="flex items-start gap-3 p-4">
                  <div className="bg-primary/10 text-primary flex h-14 w-14 shrink-0 items-center justify-center rounded-xl">
                    {o.des_url_foto ? (
                      <img
                        src={o.des_url_foto}
                        alt={o.nom_oferta}
                        className="h-full w-full rounded-xl object-cover"
                      />
                    ) : (
                      <Gift className="h-7 w-7" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate font-semibold leading-tight">{o.nom_oferta}</h3>
                      {o.val_face_cliente && (
                        <span className="text-muted-foreground text-[11px] line-through">
                          R$ {Number(o.val_face_cliente).toFixed(2)}
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground flex items-center gap-1 text-xs">
                      <Store className="h-3 w-3" />
                      {o.nom_parceiro}{' '}
                      <span className="text-[10px] uppercase opacity-60">
                        · {CATEGORIA_LABEL[o.cod_categoria] ?? o.cod_categoria}
                      </span>
                    </p>
                    {o.des_endereco && (
                      <p className="text-muted-foreground flex items-center gap-1 text-[11px]">
                        <MapPin className="h-3 w-3" />
                        {o.des_endereco}
                      </p>
                    )}
                    {o.des_oferta && (
                      <p className="text-muted-foreground line-clamp-2 text-xs">{o.des_oferta}</p>
                    )}
                    <p className="text-muted-foreground flex items-center gap-1 text-[11px]">
                      <Clock className="h-3 w-3" />
                      Valido por {o.qtd_dias_validade} dia(s) apos resgate
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-primary text-lg font-extrabold tabular-nums leading-none">
                      {o.qtd_pontos_custo}
                    </span>
                    <span className="text-muted-foreground -mt-1 text-[10px] uppercase">pts</span>
                    <Button size="sm" onClick={() => setConfirmando(o)}>
                      Resgatar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Dialog confirmacao */}
      <Dialog open={!!confirmando} onOpenChange={(open) => !open && setConfirmando(null)}>
        <DialogContent>
          {confirmando && (
            <>
              <DialogHeader>
                <DialogTitle>Confirmar resgate?</DialogTitle>
                <DialogDescription>
                  Voce vai trocar <strong>{confirmando.qtd_pontos_custo} pts</strong> por &ldquo;
                  {confirmando.nom_oferta}&rdquo; em <strong>{confirmando.nom_parceiro}</strong>.
                  Apos confirmar, voce recebe um codigo unico que vale por{' '}
                  <strong>{confirmando.qtd_dias_validade} dia(s)</strong>.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setConfirmando(null)}>
                  Cancelar
                </Button>
                <Button
                  onClick={() => resgatar.mutate(confirmando.seq_oferta_parceiro)}
                  disabled={resgatar.isPending}
                >
                  {resgatar.isPending ? 'Confirmando...' : 'Sim, resgatar'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog resgate gerado */}
      <Dialog open={!!resgateNovo} onOpenChange={(open) => !open && setResgateNovo(null)}>
        <DialogContent>
          {resgateNovo && (
            <>
              <DialogHeader>
                <DialogTitle>Codigo gerado!</DialogTitle>
                <DialogDescription>
                  Mostre este codigo no parceiro <strong>{resgateNovo.nom_parceiro}</strong>.
                </DialogDescription>
              </DialogHeader>
              <div className="bg-octa-gradient overflow-hidden rounded-2xl p-6 text-center text-white">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-85">
                  Seu codigo
                </p>
                <p className="mt-2 text-4xl font-extrabold tabular-nums tracking-wider">
                  {resgateNovo.cod_resgate_formatado}
                </p>
                <StatusBadge variant="info" className="mt-3 bg-white/20 text-white">
                  Valido ate {new Date(resgateNovo.dta_validade).toLocaleDateString('pt-BR')}
                </StatusBadge>
              </div>
              <DialogFooter>
                <Button asChild variant="outline">
                  <Link to="/meus-cupons">Ver meus cupons</Link>
                </Button>
                <Button onClick={() => setResgateNovo(null)}>Ok</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// /meus-cupons — lista de resgates do cliente
// =============================================================================

interface MeuResgate {
  seq_resgate_parceiro: number;
  cod_resgate: string;
  cod_resgate_formatado: string;
  cod_status: 'ATIVO' | 'UTILIZADO' | 'CANCELADO' | 'EXPIRADO';
  qtd_pontos_debitados: number;
  nom_oferta_snapshot: string;
  dta_resgate: string;
  dta_validade: string;
  dta_utilizacao: string | null;
  nom_parceiro: string;
  cod_categoria: string;
  des_endereco: string | null;
  des_url_foto: string | null;
}

export function MeusCuponsPage() {
  const queryClient = useQueryClient();
  const resgates = useQuery({
    queryKey: ['meus-resgates-parceiro'],
    queryFn: async () => {
      const { data, error } = await sb
        .from('vw_meus_resgates_parceiro')
        .select('*')
        .order('dta_resgate', { ascending: false });
      if (error) throw error;
      return (data ?? []) as MeuResgate[];
    },
  });

  const cancelar = useMutation({
    mutationFn: async (seq: number) => {
      const { error } = await sb.rpc('fn_cancelar_resgate_parceiro', {
        p_seq_resgate_parceiro: seq,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Resgate cancelado. Pontos devolvidos.');
      void queryClient.invalidateQueries({ queryKey: ['meus-resgates-parceiro'] });
      void queryClient.invalidateQueries({ queryKey: ['fidelidade-saldo'] });
    },
    onError: (e: Error) =>
      toast.error('Falha ao cancelar', { description: traduzirErroSupabase(e) }),
  });

  const ativos = resgates.data?.filter((r) => r.cod_status === 'ATIVO') ?? [];
  const historico = resgates.data?.filter((r) => r.cod_status !== 'ATIVO') ?? [];

  return (
    <div className="space-y-5 p-4 pb-24">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild className="text-muted-foreground -ml-2 h-8 px-2">
          <Link to="/" aria-label="Voltar">
            <ChevronLeft className="mr-1 h-4 w-4" />
            Voltar
          </Link>
        </Button>
      </div>

      <header className="space-y-1">
        <h1 className="text-display-md font-extrabold tracking-tight">Meus cupons</h1>
        <p className="text-muted-foreground text-sm">
          Codigos de resgate ativos. Mostre ao parceiro no momento da compra.
        </p>
      </header>

      {resgates.isLoading ? (
        <Skeleton className="h-32 rounded-2xl" />
      ) : (resgates.data?.length ?? 0) === 0 ? (
        <EmptyState
          variant="hero"
          icon={Ticket}
          title="Nenhum cupom ainda"
          description="Resgate seus pontos no /resgatar e seus cupons aparecem aqui."
        />
      ) : (
        <>
          {ativos.length > 0 && (
            <section className="space-y-3">
              <h2 className="octa-section-title">Ativos</h2>
              {ativos.map((r) => (
                <Card
                  key={r.seq_resgate_parceiro}
                  className="bg-octa-gradient overflow-hidden rounded-2xl text-white"
                >
                  <CardContent className="space-y-3 p-5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-85">
                        Codigo
                      </span>
                      <StatusBadge variant="success" className="bg-white/25 text-white">
                        ATIVO
                      </StatusBadge>
                    </div>
                    <p className="text-center text-3xl font-extrabold tabular-nums tracking-wider">
                      {r.cod_resgate_formatado}
                    </p>
                    <div className="text-center text-sm opacity-90">
                      <p className="font-semibold">{r.nom_oferta_snapshot}</p>
                      <p className="text-xs opacity-80">em {r.nom_parceiro}</p>
                    </div>
                    <div className="flex items-center justify-between border-t border-white/20 pt-2 text-xs">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Valido ate {new Date(r.dta_validade).toLocaleDateString('pt-BR')}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-white hover:bg-white/20"
                        onClick={() => {
                          if (
                            confirm(
                              `Cancelar resgate? ${r.qtd_pontos_debitados} pts serao devolvidos.`,
                            )
                          ) {
                            cancelar.mutate(r.seq_resgate_parceiro);
                          }
                        }}
                        disabled={cancelar.isPending}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </section>
          )}

          {historico.length > 0 && (
            <section className="space-y-2">
              <h2 className="octa-section-title">Historico</h2>
              {historico.map((r) => (
                <Card key={r.seq_resgate_parceiro} variant="flat" className="rounded-xl">
                  <CardContent className="flex items-center gap-3 p-3 text-sm">
                    <div className="bg-muted text-muted-foreground flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                      <Ticket className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold leading-tight">
                        {r.nom_oferta_snapshot}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {r.nom_parceiro} · {tempoDecorrido(r.dta_resgate)}
                      </p>
                    </div>
                    <StatusBadge
                      variant={
                        r.cod_status === 'UTILIZADO'
                          ? 'success'
                          : r.cod_status === 'CANCELADO'
                            ? 'neutral'
                            : 'warning'
                      }
                    >
                      {r.cod_status}
                    </StatusBadge>
                  </CardContent>
                </Card>
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}
